/* eslint-disable consistent-return */
import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useIdleTimer } from 'react-idle-timer';
import SessionTimeoutWarning from '../../assets/icons/session-Expired.svg';
import { useAuth } from '../../hooks/useAuth';

const AUTO_LOGOUT_SECONDS = 20 * 60; // 20 minutes

const IdleSessionHandler = () => {
  //   const navigate = useNavigate();
  const idleTimerRef = useRef<any>(null);
  const { logout, refreshToken } = useAuth();
  const [showDialog, setShowDialog] = useState(false);
  const [countdown, setCountdown] = useState(AUTO_LOGOUT_SECONDS);
  const autoLogoutTimeoutRef = useRef<any>(null);
  const countdownIntervalRef = useRef<any>(null);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const dialogStartTimeRef = useRef<number | null>(null);

  useEffect(() => {
    // Initialize BroadcastChannel for cross-tab communication
    broadcastChannelRef.current = new BroadcastChannel('idle-timer-channel');

    broadcastChannelRef.current.onmessage = (event) => {
      const { data } = event;
      if (data === 'reset') {
        // Reset timer and hide dialog when reset message received
        idleTimerRef.current?.reset();
        dialogStartTimeRef.current = null;
        setShowDialog(false);
        if (autoLogoutTimeoutRef.current) {
          clearTimeout(autoLogoutTimeoutRef.current);
          autoLogoutTimeoutRef.current = null;
        }
      } else if (data === 'logout') {
        setShowDialog(false);
        logout({ redirect: window.location.pathname });
      } else if (data && typeof data === 'object' && data.type === 'show-dialog') {
        // Sync countdown using the shared start timestamp
        dialogStartTimeRef.current = data.startTime;
        setShowDialog(true);
      }
    };

    return () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
      }
    };
  }, []);

  const logoutFun = () => {
    setShowDialog(false);

    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage('logout');
    }
    logout({ redirect: window.location.pathname });
  };

  const handleOnIdle = () => {
    const startTime = Date.now();
    dialogStartTimeRef.current = startTime;
    setShowDialog(true);
    // Broadcast show-dialog with start timestamp to other tabs
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage({ type: 'show-dialog', startTime });
    }
  };

  const handleContinue = () => {
    setShowDialog(false);
    idleTimerRef.current?.reset();
    refreshToken();
    // Broadcast reset to other tabs
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage('reset');
    }
  };

  const formatCountdown = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  useEffect(() => {
    // Always clear existing timers
    if (autoLogoutTimeoutRef.current) {
      clearTimeout(autoLogoutTimeoutRef.current);
      autoLogoutTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    if (showDialog) {
      // Calculate remaining seconds from the shared start timestamp
      const startTime = dialogStartTimeRef.current || Date.now();
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      const remainingSeconds = Math.max(AUTO_LOGOUT_SECONDS - elapsedSeconds, 0);

      setCountdown(remainingSeconds);

      if (remainingSeconds <= 0) {
        logoutFun();
        return;
      }

      // Tick countdown every second, synced to shared start time
      countdownIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        const remaining = Math.max(AUTO_LOGOUT_SECONDS - elapsed, 0);
        setCountdown(remaining);
        if (remaining <= 0) {
          clearInterval(countdownIntervalRef.current);
          logoutFun();
        }
      }, 1000);

      // Auto-logout after remaining time
      autoLogoutTimeoutRef.current = setTimeout(() => {
        logoutFun();
      }, remainingSeconds * 1000);
    }

    return () => {
      if (autoLogoutTimeoutRef.current) clearTimeout(autoLogoutTimeoutRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [showDialog]);

  useIdleTimer({
    ref: idleTimerRef,
    timeout: 1000 * 60 * 240, // 4 hours idle triggers dialog
    crossTab: true,
    onIdle: handleOnIdle,
    debounce: 500
  });

  return (
    <Dialog
      header="Session expiring soon"
      visible={showDialog}
      style={{ width: '40vw', maxWidth: '600px' }}
      onHide={() => handleContinue()}
      footer={() => (
        <div className="flex gap-2" style={{ justifyContent: 'flex-end' }}>
          <Button
            label="Log out"
            className="p-button-text"
            onClick={logoutFun}
            style={{ border: '1px solid #667085', color: '#323442', borderRadius: '6px' }}
          />

          <Button
            onClick={handleContinue}
            label="Stay Logged in"
            style={{ backgroundColor: '#551BCD' }}
          />
        </div>
      )}
    >
      <div className="card flex cus-field-container w-full">
        <img src={SessionTimeoutWarning} alt="Session Timeout Warning" className="mb-4" />
        <div>
          <span>
            Your session will expire in
            {' '}
            <strong>{formatCountdown(countdown)}</strong>
            {' '}
            due to inactivity.
          </span>
          <span>Stay signed in to continue working.</span>
        </div>
      </div>

    </Dialog>
  );
};

export default IdleSessionHandler;
